import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Plus, Search, X, Eye, Pencil, Trash2, CheckCircle,
  PackageCheck, XCircle, Upload, Image, Printer
} from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import ConfirmModal from '../../components/ConfirmModal';
import Breadcrumb from '../../components/Breadcrumb';
import FundingSourceModal from '../../components/FundingSourceModal';
import JalaliDatePicker, { JalaliDateRangeFilter } from '../../components/JalaliDatePicker';
import { purchaseService, supplierService, medicineService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali, toJalaliDateTime } from '../../utils/jalali';
import toast from 'react-hot-toast';

// ─── Searchable Supplier Lookup ───────────────────────────────────────────────
function SupplierLookup({ selected, onSelect, onClear }) {
  const [q, setQ]       = useState('');
  const [res, setRes]   = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null); const tmr = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const doSearch = useCallback(async (val) => {
    if (!val) { setRes([]); return; }
    setBusy(true);
    try { const r = await supplierService.search(val); setRes(r.data.data || []); setOpen(true); }
    catch {} finally { setBusy(false); }
  }, []);

  if (selected) return (
    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
      <div>
        <p className="text-sm font-semibold text-blue-800">{selected.name}</p>
        <p className="text-xs text-blue-500">{selected.company} · {selected.phone}</p>
      </div>
      <button type="button" onClick={onClear} className="text-blue-300 hover:text-blue-600"><X size={16} /></button>
    </div>
  );

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={q} onChange={e => { setQ(e.target.value); clearTimeout(tmr.current); tmr.current = setTimeout(() => doSearch(e.target.value), 280); }}
          onFocus={() => q && setOpen(true)}
          placeholder="Search supplier by name or phone..."
          className="w-full pl-9 pr-8 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        {busy && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
      </div>
      {open && res.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
          {res.map(s => (
            <button key={s._id} type="button" onClick={() => { onSelect(s); setQ(''); setRes([]); setOpen(false); }}
              className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-50 last:border-0">
              <p className="text-sm font-semibold text-gray-800">{s.name}</p>
              <p className="text-xs text-gray-400">{s.company} · {s.phone}</p>
            </button>
          ))}
        </div>
      )}

    </div>
  );
}

// ─── Searchable Medicine Lookup for PO items ──────────────────────────────────
function MedicineItemLookup({ onAdd }) {
  const [q, setQ] = useState(''); const [res, setRes] = useState([]); const [open, setOpen] = useState(false);
  const ref = useRef(null); const tmr = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, []);
  const doSearch = async (v) => { if (!v) return; try { const r = await medicineService.search(v); setRes(r.data.data || []); setOpen(true); } catch {} };
  const pick = (m) => { onAdd(m); setQ(''); setRes([]); setOpen(false); };
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={q} onChange={e => { setQ(e.target.value); clearTimeout(tmr.current); tmr.current = setTimeout(() => doSearch(e.target.value), 280); }}
          placeholder="Search and add medicine..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50" />
      </div>
      {open && res.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
          {res.map(m => (
            <button key={m._id} type="button" onClick={() => pick(m)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-800">{m.commercialName}</p>
                  <p className="text-xs text-gray-400">
                    {m.genericName}
                    {m.strength ? ` · ${m.strength}` : ''}
                    {m.drugForm  ? ` · ${m.drugForm}`  : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-xs font-semibold text-blue-700">{formatCurrency(m.purchasePrice)}</p>
                  <p className="text-xs text-gray-400 capitalize">{m.baseUnit}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Status badges ────────────────────────────────────────────────────────────
const statusColor   = { pending:'yellow', approved:'blue', received:'green', cancelled:'red' };
const payColor      = { paid:'green', partial:'blue', unpaid:'red' };
const statusLabel   = { pending:'Pending', approved:'Approved', received:'Received', cancelled:'Cancelled' };

// ─── Main PurchasesPage ───────────────────────────────────────────────────────
export default function PurchasesPage() {
  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [fStatus, setFS]      = useState('');
  const [fPay, setFP]         = useState('');
  const [dateFrom, setDF]     = useState('');
  const [dateTo, setDT]       = useState('');
  const [stats, setStats]     = useState(null);

  const [formOpen, setFormOpen]       = useState(false);
  const [editItem, setEditItem]       = useState(null);
  const [viewItem, setViewItem]       = useState(null);
  const [deleteTarget, setDT2]        = useState(null);
  const [cancelTarget, setCT]         = useState(null);
  const [actionTarget, setAT]         = useState(null); // {type:'approve'|'receive', item}
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [actioning, setActioning]     = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [uploadTarget, setUT]         = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [poFundingOpen, setPOFundingOpen] = useState(false);
  const [poFundingSrc,  setPOFundingSrc]  = useState(null);
  const [fundingOpen, setFundingOpen] = useState(false);
  const [fundingSource, setFundingSource] = useState('pharmacy_cash');
  const [fundingLabel,  setFundingLabel]  = useState('Pharmacy Cash');
  const [quickMedOpen, setQuickMedOpen] = useState(false);
  const [quickMedSaving, setQMSaving]   = useState(false);
  const [quickMedForm, setQMF]          = useState({ genericName:'', commercialName:'', category:'' });
  const [payTarget, setPayTarget]     = useState(null);
  const [payAmount, setPayAmount]     = useState('');
  const [payMethod, setPayMethod]     = useState('cash');
  const [paying, setPaying]           = useState(false);

  // Form state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [cartItems, setCart]  = useState([]);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax]           = useState(0);
  const [supInvoice, setSupInvoice] = useState('');
  const [notes, setNotes]     = useState('');

  const subtotal    = cartItems.reduce((s, i) => s + i.totalPrice, 0);
  const totalAmount = Math.max(0, subtotal - Number(discount) + Number(tax));

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search)  params.search = search;
      if (fStatus) params.status = fStatus;
      if (fPay)    params.paymentStatus = fPay;
      if (dateFrom) params.from = dateFrom;
      if (dateTo)   params.to   = dateTo;
      const res = await purchaseService.getAll(params);
      setData(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch { toast.error('Failed to load purchases'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const res = await purchaseService.getStats(); setStats(res.data.data); } catch {}
  };

  useEffect(() => { fetchData(); }, [page, search, fStatus, fPay, dateFrom, dateTo]); // eslint-disable-line
  useEffect(() => { fetchStats(); }, []); // eslint-disable-line

  const resetForm = () => {
    setSelectedSupplier(null); setCart([]); setDiscount(0); setTax(0);
    setSupInvoice(''); setNotes(''); setEditItem(null);
  };

  const openCreate = () => { resetForm(); setFormOpen(true); };




  const openEdit = async (row) => {
    const res = await purchaseService.getById(row._id);
    const p = res.data.data;
    setEditItem(p);
    setSelectedSupplier(p.supplier);
    setSupInvoice(p.supplierInvoiceNumber || '');
    setDiscount(p.discountAmount || 0);
    setTax(p.taxAmount || 0);
    setNotes(p.notes || '');
    setCart(p.items.map(item => ({
      medicineId:   item.medicine?._id || item.medicine,
      medicineName: item.medicineName || item.medicine?.commercialName,
      units:        item.medicine?.units || [],
      baseUnit:     item.medicine?.baseUnit || 'piece',
      quantity:     item.quantity,
      unit:         item.unit || 'piece',
      unitPrice:    item.unitPrice,
      totalPrice:   item.totalPrice,
      batchNumber:  item.batchNumber || '',
      expiryDate:   item.expiryDate ? item.expiryDate.split('T')[0] : '',
      productionDate: item.productionDate ? item.productionDate.split('T')[0] : '',
    })));
    setFormOpen(true);
  };

  const addToCart = (med) => {
    const exists = cartItems.find(i => i.medicineId === med._id);
    if (exists) {
      setCart(cartItems.map(i => i.medicineId === med._id
        ? { ...i, quantity: i.quantity+1, totalPrice: (i.quantity+1)*i.unitPrice }
        : i));
    } else {
      const defUnit = med.defaultPurchaseUnit || med.baseUnit || 'piece';
      setCart([...cartItems, {
        medicineId:   med._id,
        medicineName: med.commercialName,
        salesUnits:   med.salesUnits || [],
        baseUnit:     med.baseUnit || 'piece',
        quantity:     1,
        unit:         defUnit,
        unitPrice:    med.purchasePrice,
        totalPrice:   med.purchasePrice,
        batchNumber:  '',
        expiryDate:   '',
        productionDate: '',
      }]);
    }
  };

  const updateCart = (idx, field, val) => {
    setCart(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const u = { ...item, [field]: val };
      
      if (field === 'unit') {
        // پیدا کردن واحد جدید
        const newUnit = val;
        const med = item;
        
        if (newUnit === med.baseUnit) {
          // برگشت به واحد پایه
          u.unitPrice = med._baseSellPrice || med.unitPrice;
        } else {
          // پیدا کردن واحد فروش
          const su = (med.salesUnits || []).find(s => s.unitKey === newUnit);
          if (su && su.sellingPrice > 0) {
            u.unitPrice = su.sellingPrice;
          } else if (su) {
            // محاسبه از روی قیمت پایه
            const basePrice = med._baseSellPrice || med.unitPrice;
            u.unitPrice = basePrice * su.conversionToBase;
          }
        }
        u.totalPrice = Number(u.quantity) * Number(u.unitPrice) - Number(u.discount || 0);
      }
      
      if (field === 'quantity' || field === 'unitPrice') {
        u.totalPrice = Number(u.quantity) * Number(u.unitPrice) - Number(u.discount || 0);
      }
      
      return u;
    }));
  };

  const removeFromCart = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!selectedSupplier) return toast.error('Please select a supplier');
    if (cartItems.length === 0) return toast.error('Add at least one medicine');
    setSaving(true);
    try {
      const payload = {
        supplier: selectedSupplier._id,
        supplierInvoiceNumber: supInvoice || undefined,
        items: cartItems.map(i => ({
          medicine:       i.medicineId,
          medicineName:   i.medicineName,
          quantity:       Number(i.quantity),
          unit:           i.unit,
          unitPrice:      Number(i.unitPrice),
          totalPrice:     Number(i.totalPrice),
          batchNumber:    i.batchNumber || '',
          expiryDate:     i.expiryDate || undefined,
          productionDate: i.productionDate || undefined,
        })),
        subtotal, discountAmount: Number(discount), taxAmount: Number(tax),
        totalAmount, notes,
      };
      if (editItem) {
        await purchaseService.update(editItem._id, payload);
        toast.success('Purchase order updated');
      } else {
        await purchaseService.create(payload);
        toast.success('Purchase order created');
      }
      setFormOpen(false); resetForm(); fetchData(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleAction = async () => {
    if (!actionTarget) return;
    setActioning(true);
    try {
      if (actionTarget.type === 'approve') {
        await purchaseService.approve(actionTarget.item._id);
        toast.success('Purchase order approved');
      } else if (actionTarget.type === 'receive') {
        await purchaseService.receive(actionTarget.item._id);
        toast.success('Received! Inventory updated.');
      }
      fetchData(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setActioning(false); setAT(null); }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setDeleting(true);
    try {
      await purchaseService.cancel(cancelTarget._id, { reason: cancelReason });
      toast.success('Purchase order cancelled');
      fetchData(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setDeleting(false); setCT(null); setCancelReason(''); }
  };

  const handlePayment = async () => {
    if (!payTarget || !payAmount || Number(payAmount) <= 0) return toast.error('Enter a valid amount');
    setPaying(true);
    try {
      await purchaseService.recordPayment(payTarget._id, { amount: Number(payAmount), method: payMethod });
      toast.success('Payment recorded');
      setPayTarget(null); setPayAmount(''); setPayMethod('cash');
      fetchData(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setPaying(false); }
  };

  const handleQuickAddMed = async () => {
    if (!quickMedForm.commercialName || !quickMedForm.genericName) return toast.error('Name required');
    setQMSaving(true);
    try {
      await medicineService.create({ ...quickMedForm, isActive: true });
      toast.success(`Medicine "${quickMedForm.commercialName}" added!`);
      setQuickMedOpen(false);
      setQMF({ genericName:'', commercialName:'', category:'' });
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setQMSaving(false); }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !uploadTarget) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('images', f));
      await purchaseService.uploadImages(uploadTarget._id, fd);
      toast.success('Images uploaded');
      const res = await purchaseService.getById(uploadTarget._id);
      setViewItem(res.data.data);
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <Breadcrumb />
      <PageHeader title="Purchases" subtitle="Purchase orders and supplier invoices"
        actions={<Button onClick={()=>setPOFundingOpen(true)}><Plus size={16} /> New Purchase Order</Button>} />

      {/* Stats Cards */}
      {stats?.totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {[
            { label:'Total Orders', value: stats.totals.count || 0, color:'blue' },
            { label:'Total Amount', value: formatCurrency(stats.totals.total || 0), color:'purple' },
            { label:'Total Paid', value: formatCurrency(stats.totals.paid || 0), color:'green' },
            { label:'Outstanding', value: formatCurrency((stats.totals.total||0)-(stats.totals.paid||0)), color:'red' },
          ].map(s => (
            <div key={s.label} className={`bg-white rounded-xl border p-4 shadow-sm ${s.color==='red'&&(stats.totals.total||0)>(stats.totals.paid||0)?'border-red-200':'border-gray-200'}`}>
              <p className="text-xs text-gray-500 mb-1">{s.label}</p>
              <p className={`text-lg font-bold ${s.color==='red'?'text-red-600':s.color==='green'?'text-green-700':s.color==='purple'?'text-purple-700':'text-blue-700'}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-40">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search PO# or supplier invoice..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={fStatus} onChange={e => setFS(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={fPay} onChange={e => setFP(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Payments</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
          <option value="unpaid">Unpaid</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => setDF(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <input type="date" value={dateTo} onChange={e => setDT(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        {(search||fStatus||fPay||dateFrom||dateTo) && (
          <button onClick={() => { setSearch(''); setFS(''); setFP(''); setDF(''); setDT(''); }} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"><X size={12}/>Clear</button>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">PO Number</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Supplier Invoice</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Supplier</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Items</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Total</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Paid</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? Array(5).fill(0).map((_, i) => (
              <tr key={i}>{Array(9).fill(0).map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td>)}</tr>
            )) : data.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-gray-400">No purchase orders found</td></tr>
            ) : data.map(row => (
              <tr key={row._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs text-blue-700 font-medium">{row.poNumber}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{row.supplierInvoiceNumber || '-'}</td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-800">{row.supplier?.name}</p>
                  <p className="text-xs text-gray-400">{row.supplier?.company}</p>
                </td>
                <td className="px-4 py-3 text-center text-gray-500">{row.items?.length}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatCurrency(row.totalAmount)}</td>
                <td className="px-4 py-3 text-right text-gray-500">{formatCurrency(row.paidAmount)}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <Badge color={statusColor[row.status]||'gray'}>{statusLabel[row.status]}</Badge>
                    <Badge color={payColor[row.paymentStatus]||'gray'}>{row.paymentStatus}</Badge>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">{toJalali(row.createdAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => purchaseService.getById(row._id).then(r => setViewItem(r.data.data))}
                      className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="View"><Eye size={13}/></button>
                    {row.status === 'pending' && <>
                      <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-yellow-50 text-gray-400 hover:text-yellow-600" title="Edit"><Pencil size={13}/></button>
                      <button onClick={() => setAT({ type:'approve', item: row })} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Approve"><CheckCircle size={13}/></button>
                    </>}
                    {row.status === 'approved' && (
                      <button onClick={() => setAT({ type:'receive', item: row })} className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600" title="Mark Received"><PackageCheck size={13}/></button>
                    )}
                    {!['received','cancelled'].includes(row.status) && (
                      <button onClick={() => { setCT(row); setCancelReason(''); }} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Cancel"><XCircle size={13}/></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Pagination */}
        {total > 20 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">{((page-1)*20)+1}–{Math.min(page*20,total)} of {total}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button>
              {Array.from({length: Math.min(5,Math.ceil(total/20))},(_,i)=>{const p=page<=3?i+1:page-2+i;if(p<1||p>Math.ceil(total/20))return null;return<button key={p} onClick={()=>setPage(p)} className={`w-8 h-8 text-xs rounded border ${p===page?'bg-blue-600 text-white border-blue-600':'border-gray-200 hover:bg-gray-50'}`}>{p}</button>;})}
              <button onClick={() => setPage(p => p+1)} disabled={page*20>=total} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {data.map(row => (
          <div key={row._id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-xs font-mono text-blue-700">{row.poNumber}</p>
                <p className="text-sm font-semibold text-gray-800">{row.supplier?.name}</p>
                {row.supplierInvoiceNumber && <p className="text-xs text-gray-400">Inv: {row.supplierInvoiceNumber}</p>}
              </div>
              <div className="flex flex-col gap-1 items-end">
                <Badge color={statusColor[row.status]||'gray'}>{statusLabel[row.status]}</Badge>
                <Badge color={payColor[row.paymentStatus]||'gray'}>{row.paymentStatus}</Badge>
              </div>
            </div>
            <div className="flex justify-between text-sm mb-3">
              <span className="text-gray-400">{toJalali(row.createdAt)} · {row.items?.length} items</span>
              <span className="font-bold text-gray-900">{formatCurrency(row.totalAmount)}</span>
            </div>
            <div className="flex gap-2 pt-2 border-t border-gray-50">
              <button onClick={() => purchaseService.getById(row._id).then(r => setViewItem(r.data.data))} className="flex-1 text-xs py-1.5 rounded bg-blue-50 text-blue-700 font-medium">View</button>
              {row.status === 'pending' && <button onClick={() => setAT({type:'approve',item:row})} className="flex-1 text-xs py-1.5 rounded bg-yellow-50 text-yellow-700 font-medium">Approve</button>}
              {row.status === 'approved' && <button onClick={() => setAT({type:'receive',item:row})} className="flex-1 text-xs py-1.5 rounded bg-green-50 text-green-700 font-medium">Receive</button>}
              {!['received','cancelled'].includes(row.status) && <button onClick={() => {setCT(row);setCancelReason('');}} className="flex-1 text-xs py-1.5 rounded bg-red-50 text-red-700 font-medium">Cancel</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Form Modal */}
      <Modal isOpen={formOpen} onClose={() => { setFormOpen(false); resetForm(); }} title={editItem ? 'Edit Purchase Order' : 'New Purchase Order'} size="xl">
        <div className="space-y-4">
          {/* Supplier + Invoice */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Supplier" required>
              <SupplierLookup selected={selectedSupplier} onSelect={setSelectedSupplier} onClear={() => setSelectedSupplier(null)} />
            </FormField>
            <FormField label="Supplier Invoice Number">
              <Input value={supInvoice} onChange={e => setSupInvoice(e.target.value)} placeholder="Invoice no. from supplier" />
            </FormField>
          </div>

          {/* Medicine Search */}
          <FormField label="Add Medicine">
            <MedicineItemLookup onAdd={addToCart} />
          </FormField>

          {/* Items Table */}
          {cartItems.length > 0 && (
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Medicine</th>
                      <th className="text-center px-2 py-2 text-xs font-medium text-gray-400 w-16">Qty</th>
                      <th className="text-left px-2 py-2 text-xs font-medium text-gray-400 w-24">Unit</th>
                      <th className="text-right px-2 py-2 text-xs font-medium text-gray-400 w-24">Unit Price</th>
                      <th className="text-left px-2 py-2 text-xs font-medium text-gray-400 w-24">Batch</th>
                      <th className="text-left px-2 py-2 text-xs font-medium text-gray-400 w-28">Expiry</th>
                      <th className="text-right px-2 py-2 text-xs font-medium text-gray-400 w-24">Total</th>
                      <th className="w-8"/>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {cartItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2"><p className="text-xs font-medium text-gray-800">{item.medicineName}</p></td>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1 justify-center">
                            <button type="button" onClick={() => updateCart(idx,'quantity',Math.max(1,item.quantity-1))} className="w-5 h-5 rounded border text-gray-500 hover:bg-gray-100 text-xs flex items-center justify-center">-</button>
                            <span className="w-6 text-center text-xs">{item.quantity}</span>
                            <button type="button" onClick={() => updateCart(idx,'quantity',item.quantity+1)} className="w-5 h-5 rounded border text-gray-500 hover:bg-gray-100 text-xs flex items-center justify-center">+</button>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <select value={item.unit} onChange={e => updateCart(idx,'unit',e.target.value)}
                            className="text-xs border border-gray-200 rounded px-1 py-1 w-full bg-white focus:outline-none focus:ring-1 focus:ring-blue-400">
                            <option value={item.baseUnit}>{item.baseUnit} (base)</option>
                            {(item.salesUnits||[]).map(u=>(
                              <option key={u.unitKey} value={u.unitKey}>{u.unitName} (={u.conversionToBase} {item.baseUnit})</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={item.unitPrice} min="0" step="0.01"
                            onChange={e => updateCart(idx,'unitPrice',e.target.value)}
                            className="text-xs border border-gray-200 rounded px-2 py-1 w-full text-right focus:outline-none focus:ring-1 focus:ring-blue-400"/>
                        </td>
                        <td className="px-2 py-2">
                          <input value={item.batchNumber} onChange={e => updateCart(idx,'batchNumber',e.target.value)}
                            className="text-xs border border-gray-200 rounded px-2 py-1 w-full focus:outline-none" placeholder="Batch"/>
                        </td>
                        <td className="px-2 py-2">
                          <input type="date" value={item.expiryDate} onChange={e => updateCart(idx,'expiryDate',e.target.value)}
                            className="text-xs border border-gray-200 rounded px-2 py-1 w-full focus:outline-none"/>
                        </td>
                        <td className="px-2 py-2 text-right text-xs font-semibold text-gray-800">{formatCurrency(item.totalPrice)}</td>
                        <td className="px-1 py-2"><button type="button" onClick={() => removeFromCart(idx)} className="text-red-300 hover:text-red-600"><X size={13}/></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <FormField label="Notes">
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Notes"/>
              </FormField>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-sm border border-gray-100">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Discount</span>
                <input type="number" value={discount} min="0" onChange={e=>setDiscount(e.target.value)} className="w-24 text-right text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none"/>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Tax</span>
                <input type="number" value={tax} min="0" onChange={e=>setTax(e.target.value)} className="w-24 text-right text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none"/>
              </div>
              <div className="flex justify-between font-bold text-base border-t pt-2"><span>Total</span><span className="text-blue-700">{formatCurrency(totalAmount)}</span></div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <Button variant="secondary" type="button" onClick={() => { setFormOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSubmit} loading={saving} disabled={cartItems.length===0||!selectedSupplier}>
              {editItem ? 'Update Order' : 'Create Purchase Order'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title={`PO Details — ${viewItem?.poNumber}`} size="xl">
        {viewItem && (
          <div className="space-y-4">
            {/* Header info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-1">
                <p><strong>Supplier:</strong> {viewItem.supplier?.name}</p>
                <p><strong>Company:</strong> {viewItem.supplier?.company || '-'}</p>
                <p><strong>Phone:</strong> {viewItem.supplier?.phone || '-'}</p>
              </div>
              <div className="space-y-1 text-right">
                <p><strong>PO #:</strong> {viewItem.poNumber}</p>
                <p><strong>Supplier Inv #:</strong> {viewItem.supplierInvoiceNumber || '-'}</p>
                <p><strong>Date:</strong> {toJalaliDateTime(viewItem.createdAt)}</p>
                <div className="flex gap-2 justify-end mt-1">
                  <Badge color={statusColor[viewItem.status]||'gray'}>{statusLabel[viewItem.status]}</Badge>
                  <Badge color={payColor[viewItem.paymentStatus]||'gray'}>{viewItem.paymentStatus}</Badge>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="border border-gray-100 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">#</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Medicine</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Qty</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Unit</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Price</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Batch</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Expiry</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Total</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {viewItem.items?.map((item,i) => (
                    <tr key={i}><td className="px-3 py-2 text-gray-400">{i+1}</td>
                      <td className="px-3 py-2"><p className="font-medium">{item.medicineName||item.medicine?.commercialName}</p></td>
                      <td className="px-3 py-2 text-center">{item.quantity}</td>
                      <td className="px-3 py-2 text-center capitalize">{item.unit}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                      <td className="px-3 py-2 text-xs text-gray-400">{item.batchNumber||'-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-400">{item.expiryDate?toJalali(item.expiryDate):'-'}</td>
                      <td className="px-3 py-2 text-right font-semibold">{formatCurrency(item.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-52 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(viewItem.subtotal)}</span></div>
                {viewItem.discountAmount>0&&<div className="flex justify-between text-red-500"><span>Discount</span><span>-{formatCurrency(viewItem.discountAmount)}</span></div>}
                {viewItem.taxAmount>0&&<div className="flex justify-between"><span>Tax</span><span>+{formatCurrency(viewItem.taxAmount)}</span></div>}
                <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span className="text-blue-700">{formatCurrency(viewItem.totalAmount)}</span></div>
                <div className="flex justify-between text-gray-500"><span>Paid</span><span className="text-green-600">{formatCurrency(viewItem.paidAmount)}</span></div>
                {viewItem.totalAmount-viewItem.paidAmount>0&&<div className="flex justify-between font-semibold text-red-600"><span>Outstanding</span><span>{formatCurrency(viewItem.totalAmount-viewItem.paidAmount)}</span></div>}
              </div>
            </div>

            {/* Invoice Images */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700">Invoice Images ({viewItem.invoiceImages?.length || 0})</p>
                <label className="cursor-pointer flex items-center gap-1.5 text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">
                  <Upload size={13}/> Upload
                  <input type="file" multiple accept="image/*" className="hidden"
                    onChange={e => { setUT(viewItem); handleUpload(e); }} />
                </label>
              </div>
              {viewItem.invoiceImages?.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {viewItem.invoiceImages.map((img, i) => (
                    <a key={i} href={`http://localhost:5000${img}`} target="_blank" rel="noreferrer"
                      className="block aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors">
                      <img src={`http://localhost:5000${img}`} alt={`Invoice ${i+1}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400 text-center py-4">No images uploaded yet</p>}
            </div>

            {/* Notes & Actions */}
            {viewItem.notes && <p className="text-xs text-gray-500">Notes: {viewItem.notes}</p>}
            <div className="flex justify-between pt-2 border-t border-gray-100">
              <div className="flex gap-2">
                {viewItem.status==='pending'&&<Button size="sm" onClick={() => { setAT({type:'approve',item:viewItem}); setViewItem(null); }}><CheckCircle size={14}/> Approve</Button>}
                {viewItem.status==='approved'&&<Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { setAT({type:'receive',item:viewItem}); setViewItem(null); }}><PackageCheck size={14}/> Mark Received</Button>}
                {!['received','cancelled'].includes(viewItem.status)&&<Button size="sm" variant="danger" onClick={() => { setCT(viewItem); setViewItem(null); }}><XCircle size={14}/> Cancel</Button>}
              </div>
              <Button variant="secondary" onClick={() => setViewItem(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={!!payTarget} onClose={()=>setPayTarget(null)} title="Record Payment" size="sm">
        {payTarget && (
          <div className="space-y-4">
            <div className="bg-orange-50 rounded-xl p-4 flex justify-between items-center">
              <div><p className="text-xs text-gray-500">Outstanding Balance</p><p className="text-xl font-bold text-orange-700">{formatCurrency(payTarget.totalAmount - payTarget.paidAmount)}</p></div>
              <div><p className="text-xs text-gray-500">PO #</p><p className="text-xs font-mono text-gray-700">{payTarget.poNumber}</p></div>
            </div>
            <FormField label="Amount">
              <Input type="number" step="0.01" min="0" max={payTarget.totalAmount - payTarget.paidAmount}
                value={payAmount} onChange={e=>setPayAmount(e.target.value)} placeholder="0.00"/>
            </FormField>
            <FormField label="Method">
              <Select value={payMethod} onChange={e=>setPayMethod(e.target.value)}>
                <option value="cash">Cash</option><option value="bank">Bank</option>
                <option value="card">Card</option><option value="mobile">Mobile</option>
              </Select>
            </FormField>
            <div className="flex gap-2">
              <button onClick={()=>setPayAmount(String(payTarget.totalAmount-payTarget.paidAmount))}
                className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">Pay Full</button>
              <Button onClick={handlePayment} loading={paying} className="ml-auto bg-green-600 hover:bg-green-700 text-white">
                <DollarSign size={15}/> Record Payment
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Approve/Receive Confirm */}
      <ConfirmModal isOpen={!!actionTarget} onClose={() => setAT(null)} onConfirm={handleAction} loading={actioning}
        variant={actionTarget?.type==='receive' ? 'primary' : 'primary'}
        title={actionTarget?.type==='approve' ? 'Approve Purchase Order' : 'Mark as Received'}
        message={actionTarget?.type==='approve'
          ? `Approve PO "${actionTarget?.item?.poNumber}"?`
          : `Mark PO "${actionTarget?.item?.poNumber}" as received? This will add all items to inventory and update supplier debt.`}
        confirmLabel={actionTarget?.type==='approve' ? 'Approve' : 'Mark Received'} />

      {/* Cancel Confirm */}
      <Modal isOpen={!!cancelTarget} onClose={() => setCT(null)} title="Cancel Purchase Order" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Cancel PO <strong>{cancelTarget?.poNumber}</strong>?</p>
          <FormField label="Reason (optional)">
            <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
              placeholder="Reason for cancellation"/>
          </FormField>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCT(null)}>Back</Button>
            <Button variant="danger" onClick={handleCancel} loading={deleting}>Cancel Order</Button>
          </div>
        </div>
      </Modal>

      {/* ── Funding Source Modal ── */}
      <FundingSourceModal
        isOpen={poFundingOpen}
        onClose={()=>setPOFundingOpen(false)}
        title="Select Funding Source"
        onSelect={(src)=>{
          setPOFundingSrc(src);
          setPOFundingOpen(false);
          openCreate();
        }}
      />
    </div>
  );
}